import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';

const Card = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.large};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.md};
  box-shadow: ${theme.shadows.subtle};
  border: 1px solid ${theme.colors.softGray};
  transition: all 0.2s ease-in-out;

  &:hover {
    box-shadow: ${theme.shadows.medium};
    border-color: ${theme.colors.brandYellow};
    transform: translateY(-2px);
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.sm};
`;

const LevelBadge = styled.div`
  background-color: ${theme.colors.white};
  color: ${theme.colors.deepNavy};
  border: 1px solid ${theme.colors.softGray};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.medium};
  font-size: ${theme.typography.fontSizes.small};
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const CauseBadge = styled.div`
  background-color: ${theme.colors.iceBlue};
  color: ${theme.colors.deepNavy};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.medium};
  font-size: ${theme.typography.fontSizes.small};
  font-weight: 500;
`;

const Title = styled.h3`
  font-family: ${theme.typography.headingFont};
  font-size: ${theme.typography.fontSizes.large};
  color: ${theme.colors.deepNavy};
  margin: ${theme.spacing.sm} 0;
  font-weight: bold;
`;

const Description = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.fontSizes.medium};
  color: ${theme.colors.slateGray};
  line-height: 1.6;
  margin-bottom: ${theme.spacing.md};
`;

const Metadata = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
  padding: ${theme.spacing.sm} 0;
  border-top: 1px solid ${theme.colors.softGray};
  border-bottom: 1px solid ${theme.colors.softGray};
`;

const MetadataItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-size: ${theme.typography.fontSizes.small};
  color: ${theme.colors.deepNavy};
`;

const MetadataIcon = styled.span`
  font-size: 14px;
  color: ${theme.colors.deepNavy};
  font-weight: 600;
`;

const LevelIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

const TryButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.brandYellow} 0%, #FFC700 100%);
  color: ${theme.colors.deepNavy};
  border: none;
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.fontSizes.medium};
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 4px 8px rgba(255, 209, 0, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(255, 209, 0, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const getLevelIconPath = (level) => {
  if (level === 'Streetlight') return '/icons/icon_streetlight.png';
  if (level === 'Daylight') return '/icons/icon_daylight.png';
  return '/icons/icon_sunshield.png';
};

const SuggestionCard = ({ suggestion, onTryActivity }) => {
  const { t } = useTranslation();
  return (
    <Card>
      <Header>
        <LevelBadge level={suggestion.level}>
          <LevelIcon src={getLevelIconPath(suggestion.level)} alt={suggestion.level} />
          {suggestion.level}
        </LevelBadge>
        <CauseBadge>{suggestion.causeName}</CauseBadge>
      </Header>

      <Title>{suggestion.title}</Title>
      <Description>{suggestion.description}</Description>

      <Metadata>
        <MetadataItem>
          <MetadataIcon>◷</MetadataIcon> {suggestion.timeCommitment}
        </MetadataItem>
        <MetadataItem>
          <MetadataIcon>◉</MetadataIcon> {suggestion.location}
        </MetadataItem>
        <MetadataItem>
          <MetadataIcon>◎</MetadataIcon> {suggestion.groupSize}
        </MetadataItem>
      </Metadata>

      <TryButton onClick={() => onTryActivity(suggestion)}>
        {t('inspired.tryThis')}
      </TryButton>
    </Card>
  );
};

export default SuggestionCard;
